import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, ArrowLeft } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { useI18n } from "@/i18n/I18nContext";
import { INSIGHTS } from "@/constants/testIds";
import { fetchInsights, formatDate } from "@/lib/insightsApi";

const accentBg = (a) =>
  a === "gold"
    ? "from-amber-100 via-amber-50 to-white"
    : a === "navy"
    ? "from-slate-100 via-slate-50 to-white"
    : "from-cyan-100 via-cyan-50 to-white";

export default function InsightsListPage() {
  const { t, lang } = useI18n();
  const i = t.insights;
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = i.listPageTitle;
    window.scrollTo(0, 0);
    fetchInsights()
      .then(setArticles)
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [i.listPageTitle]);

  return (
    <main data-testid={INSIGHTS.listPage} className="min-h-screen bg-background text-foreground">
      <Navbar />

      <section className="pt-32 lg:pt-40 pb-16 bg-white relative overflow-hidden">
        <div className="absolute inset-0 -z-10 grid-bg opacity-50" />
        <div className="container-x">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-navy transition-colors mb-8"
          >
            <ArrowLeft className="h-4 w-4" /> {i.backToHome}
          </Link>
          <span className="text-overline">{i.overline}</span>
          <h1 className="mt-4 font-display font-semibold text-navy text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            {i.listPageHeading}
          </h1>
          <p className="mt-6 max-w-3xl text-base sm:text-lg text-slate-700 leading-relaxed">
            {i.listPageSubheading}
          </p>
        </div>
      </section>

      <section className="pb-24 bg-white">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading
              ? Array.from({ length: 6 }).map((_, k) => (
                  <div
                    key={k}
                    className="h-80 rounded-2xl border border-slate-200 bg-slate-50 animate-pulse"
                  />
                ))
              : articles.map((a) => (
                  <Link
                    key={a.slug}
                    to={`/insights/${a.slug}`}
                    data-testid={`${INSIGHTS.card}-${a.slug}`}
                    className="group block rounded-2xl border border-slate-200 bg-white hover:shadow-xl hover:border-navy/30 transition-all overflow-hidden"
                  >
                    <div
                      className={`aspect-[16/9] bg-gradient-to-br ${accentBg(
                        a.accent
                      )} relative`}
                    >
                      <div className="absolute inset-0 grid-bg opacity-50" />
                      <span className="absolute top-4 left-4 inline-flex items-center px-2.5 py-1 rounded-full bg-white/85 backdrop-blur text-[11px] font-semibold uppercase tracking-[0.18em] text-navy border border-slate-200">
                        {a.tag[lang]}
                      </span>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatDate(a.date, lang)}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" />
                          {a.reading_minutes} {i.minRead}
                        </span>
                      </div>
                      <h2 className="mt-4 font-display text-xl font-semibold text-navy leading-snug group-hover:text-cyan-700 transition-colors">
                        {a.titles[lang]}
                      </h2>
                      <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">
                        {a.excerpts[lang]}
                      </p>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-navy group-hover:gap-2.5 transition-all">
                        {i.readMore} <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </Link>
                ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
